import { useEffect, useState } from "react";
import axios from "axios";
import { API, fmtErr } from "../lib/pos";
import { Check, Download } from "lucide-react";

export function BillSettingsAdmin({ onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/bill`).then((r) => setForm(r.data)).catch((e) => alert(fmtErr(e)));
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      const payload = {
        title: form.title, subtitle: form.subtitle, address: form.address,
        phone: form.phone, website: form.website, email: form.email,
        fssai: form.fssai, footer_note: form.footer_note, printer_name: form.printer_name,
      };
      if ((form.manager_pin || "").trim()) payload.manager_pin = form.manager_pin.trim();
      await axios.put(`${API}/settings/bill`, payload);
      setSaved(true);
      if (onSaved) onSaved();
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert(fmtErr(e)); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="workspace"><p className="muted">Loading settings…</p></div>;

  return (
    <div className="workspace">
      <div className="page-head">
        <div>
          <p className="eyebrow">MANAGER TOOLS / SETTINGS</p>
          <h1>Bill settings</h1>
          <p className="muted">These details print on every KOT and Bill. Update once — they take effect on the next print.</p>
        </div>
        <button data-testid="save-bill-settings-button" className="primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>

      <div className="panel" data-testid="bill-settings-form">
        <div className="settings-form">
          <label className="field wide">Title
            <input data-testid="settings-title-input" value={form.title || ""} onChange={(e) => upd("title", e.target.value)} />
          </label>
          <label className="field wide">Sub-title
            <input data-testid="settings-subtitle-input" value={form.subtitle || ""} onChange={(e) => upd("subtitle", e.target.value)} />
          </label>
          <label className="field wide">Address
            <input data-testid="settings-address-input" value={form.address || ""} onChange={(e) => upd("address", e.target.value)} />
          </label>
          <label className="field">Phone no.
            <input data-testid="settings-phone-input" value={form.phone || ""} onChange={(e) => upd("phone", e.target.value)} />
          </label>
          <label className="field">Website
            <input data-testid="settings-website-input" value={form.website || ""} onChange={(e) => upd("website", e.target.value)} />
          </label>
          <label className="field">Email
            <input data-testid="settings-email-input" type="email" value={form.email || ""} onChange={(e) => upd("email", e.target.value)} />
          </label>
          <label className="field">FSSAI no.
            <input data-testid="settings-fssai-input" value={form.fssai || ""} onChange={(e) => upd("fssai", e.target.value)} />
          </label>
          <label className="field wide">Footer note
            <input data-testid="settings-footer-input" value={form.footer_note || ""} onChange={(e) => upd("footer_note", e.target.value)} />
          </label>
          <label className="field">Preferred printer name
            <input data-testid="settings-printer-input" value={form.printer_name || ""} onChange={(e) => upd("printer_name", e.target.value)} placeholder="BILLING" />
          </label>
          <label className="field">Manager PIN <span style={{ color: "var(--muted)", fontWeight: 400, letterSpacing: 0, textTransform: "none", fontSize: 10 }}>· 4 digits · gates 30% & 100% discount slabs</span>
            <input
              data-testid="settings-pin-input"
              value={form.manager_pin || ""}
              maxLength={4}
              inputMode="numeric"
              pattern="[0-9]{4}"
              placeholder="1234"
              onChange={(e) => upd("manager_pin", e.target.value.replace(/\D/g, "").slice(0, 4))}
            />
          </label>
        </div>

        {saved && <p className="success" data-testid="settings-saved" style={{ marginTop: 14 }}><Check size={14} /> Saved · new bills will use these details</p>}

        <div className="settings-note" data-testid="printer-setup-note">
          <b>Silent printing to “{form.printer_name || "BILLING"}”</b> — browsers cannot pick a printer from JavaScript. To skip the print dialog completely and route every ticket to your <code>{form.printer_name || "BILLING"}</code> device:
          <ol style={{ margin: "8px 0 0 18px", padding: 0, lineHeight: 1.7 }}>
            <li>On the counter PC, open <b>Settings → Printers &amp; scanners</b>, right-click <code>{form.printer_name || "BILLING"}</code> and select <b>“Set as default”</b>.</li>
            <li>Launch Chrome / Edge with the flag <code>--kiosk-printing</code>. Example shortcut target: <code>chrome.exe --kiosk-printing https://pos.shimlafoodcourt.com</code></li>
            <li>Every KOT / bill will now print silently to <code>{form.printer_name || "BILLING"}</code> without a dialog. On restart, the same flag keeps working.</li>
          </ol>
        </div>
      </div>

      <BackupSection />
    </div>
  );
}


function BackupSection() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [last, setLast] = useState(null); // {filename, docs, collections, size}

  const download = async () => {
    setBusy(true); setErr("");
    try {
      const r = await axios.get(`${API}/admin/db-dump`, { responseType: "blob" });
      // Extract filename from Content-Disposition header if present
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m ? m[1] : `database_dump_${new Date().toISOString().replace(/[:.]/g, "-")}.zip`;
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 500);
      setLast({
        filename,
        docs: r.headers["x-dump-total-documents"],
        collections: r.headers["x-dump-collections"],
        size: blob.size,
      });
    } catch (e) { setErr(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const fmtSize = (n) => n > 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(2)} MB`
    : `${(n / 1024).toFixed(1)} KB`;

  return (
    <div className="panel" data-testid="backup-section" style={{ marginTop: 26 }}>
      <div className="section-line">
        <h2>Database backup</h2>
        <span className="eyebrow">MONGODUMP</span>
      </div>
      <p className="muted" style={{ fontSize: 12, lineHeight: 1.6, marginTop: 8 }}>
        Download a full <code>mongodump</code>-compatible archive of the entire database (every collection as a
        <code> .bson</code> file inside a ZIP). Restore later with:
        <br />
        <code style={{ display: "inline-block", marginTop: 6, fontSize: 11 }}>
          unzip &lt;file&gt;.zip -d ./restore &amp;&amp; mongorestore ./restore/&lt;db_name&gt;
        </code>
        <br />
        Store dumps in a safe location — they contain every user, order, payment and setting.
      </p>
      <button
        data-testid="backup-download-button"
        className="primary"
        onClick={download}
        disabled={busy}
        style={{ marginTop: 12 }}
      >
        <Download size={14} />
        {busy ? "Preparing dump…" : "Download database dump"}
      </button>
      {err && <div className="auth-error" data-testid="backup-error" style={{ marginTop: 12 }}>{err}</div>}
      {last && (
        <div className="auth-success" data-testid="backup-result" style={{ marginTop: 12 }}>
          Downloaded <b>{last.filename}</b> · {last.collections} collections · {last.docs} documents · {fmtSize(last.size)}
        </div>
      )}
    </div>
  );
}


/* ---------- Shell ---------- */
